import React, { useState, useEffect, useRef, useCallback } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import HeaderTitle from "../components/HeaderTitle";
import DisasterMgtService from "../service/citizen/DisasterMgtService";
import * as XLSX from "xlsx";

import { ChevronDown } from "lucide-react";
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Inject,
  Page,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import { collection, getDocs, query, where } from "firebase/firestore";
import { doc, getDoc } from "firebase/firestore";
import { DropDownListComponent } from "@syncfusion/ej2-react-dropdowns";
import { decrypt } from "../service/encryption/Encryption";
import LockImage from "../../src/assets/lockbox.png";
import { useAuth } from "../components/AuthContext";

const ReportGenerator = ({ className }) => {
  const [userData, setUserData] = useState("");
  const [activeData, setActiveData] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [disasterNames, setDisasterNames] = useState([]);
  const [selectedDisaster, setSelectedDisaster] = useState("");
  const [selectedDisasterId, setSelectedDisasterId] = useState("");
  const dropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCounties, setSelectedCounties] = useState([]);
  const auth = getAuth();
  const user = auth.currentUser;
  const [showHouseholdsModal, setShowHouseholdsModal] = useState(false);
  const [enrichedUsers, setEnrichedUsers] = useState([]);
  const [selectedCardType, setSelectedCardType] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("injury");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [enteredPIN, setEnteredPIN] = useState("");
  const [correctPIN, setCorrectPIN] = useState("1111");
  const [inputValues, setInputValues] = useState(["", "", "", ""]);
  const [wrongPinMessage, setWrongPinMessage] = useState(false);
  const inputRefs = useRef([]);
  const [showPinInstructions, setShowPinInstructions] = useState(
    localStorage.getItem("pinMessageShown") !== "true"
  );
  const { role } = useAuth();

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const questionKeys = {
    injury:
      "What is the worst level of injury sustained by a member of your household?",
    disasterImpact: "Was the impacted address your primary address?",
    homeDamage: "How would you describe the level of damage to your home?",
    livingSituation: "What is your current living situation?",
    housingType: "What type of structure is the impacted home?",
    medicalNeeds:
      "Do you or anyone in your household have mobility or medical equipment that requires electricity?",
    tornadoShelter: "Did you have to take shelter during the tornado?",
    propertyDamageType: "What parts of the property had damage?",
    immediateNeeds: "IMMEDIATE NEEDS Check all that apply",
    waterDepth: "What is the estimated depth of water that entered the home?",
    wellWater: "Do you have well water?",
    vehiclesAffected: "How many vehicles were impacted?",
    transportationNeeds: "Do you have access to reliable transportation?",

    standingWater: "Is there standing water still inside the home?",
    debrisRemoval: "Do you need immediate assistance with debris removal?",
    fallenTree:
      "Was your home hit by debris or a fallen tree? (Select all that apply)",
    majorAppliances:
      "Have major appliances been affected by flooding? (Select all that apply)",
    runningWater: "Does the impacted home have running water?",
  };

  //DATA FROM DISASTER MTG SERVICE
  const [{ affectedUserIds }, setGraphData] = useState({
    affectedUserIds: {},
  });

  const selectedUserId = sessionStorage.getItem("userId");

  //FETCH USER DATA
  useEffect(() => {
    const fetchUserRole = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user && selectedUserId) {
        const userDoc = await getDoc(doc(db, "users", selectedUserId));
        if (userDoc.exists()) {
          const role = userDoc.data().user_role;
        }
      }
    };

    fetchUserRole();
  }, []);

  const pageSettings = {
    pageSize: 10,
    pageCount: 5,
    enableQueryString: false,
  };

  //FETCH DISASTERS
  useEffect(() => {
    const fetchUserData = async () => {
      if (user && selectedUserId) {
        const userDoc = await getDoc(doc(db, "users", selectedUserId));

        if (userDoc.exists()) {
          const role = userDoc.data().user_role;
          const userData = userDoc.data();
          setUserRole(role);

          let updatedUserData = userData;

          if (role === "center") {
            const centerDoc = await getDoc(
              doc(db, "centers", userData.center_id)
            );
            if (centerDoc.exists()) {
              updatedUserData = centerDoc.data();
            }
          } else if (role === "partner") {
            const partnerDoc = await getDoc(
              doc(db, "partners", userData?.partner_id)
            );
            if (partnerDoc.exists()) {
              updatedUserData = partnerDoc.data();
            }
          }

          setUserData(updatedUserData);

          try {
            let disasters = [];
            let activeData = [];

            if (role === "partner") {
              if (!userData?.center_id) {
                console.log("No center_id available for partner");
                setLoading(false);
                return;
              }

              const centerIds = Array.isArray(userData.center_id)
                ? userData.center_id
                : [userData.center_id];

              if (centerIds.length === 0) {
                console.log("No center IDs available");
                setLoading(false);
                return;
              }

              const counties = updatedUserData?.area_serviced_county || [];

              if (counties.length === 0) {
                console.log("No counties to query");
                setLoading(false);
                return;
              }

              const chunks = [];
              for (let i = 0; i < counties.length; i += 10) {
                chunks.push(counties.slice(i, i + 10));
              }

              const seenIds = new Set();

              for (const chunk of chunks) {
                const q = query(
                  collection(db, "declared-disaster"),
                  where("disaster_status", "==", "active"),
                  where("is_deleted", "==", false),
                  where("center_id", "in", centerIds),
                  where("affected_counties", "array-contains-any", chunk)
                );

                const snapshot = await getDocs(q);

                snapshot.forEach((doc) => {
                  if (!seenIds.has(doc.id)) {
                    seenIds.add(doc.id);
                    disasters.push({
                      id: doc.id,
                      name: doc.data().disaster_name,
                    });

                    activeData.push({
                      disaster_id: doc.id,
                      disaster_name: doc.data().disaster_name,
                      disaster_status: doc.data().disaster_status,
                      start_date: doc.data().start_date
                        ? doc
                            .data()
                            .start_date.toDate()
                            .toLocaleDateString("en-US", {
                              month: "2-digit",
                              day: "2-digit",
                              year: "numeric",
                            })
                        : null,
                      county: doc.data().affected_counties,
                    });
                  }
                });
              }
            } else if (role === "center") {
              if (!userData?.center_id) {
                console.log("No center_id available for center");
                setLoading(false);
                return;
              }

              const q = query(
                collection(db, "declared-disaster"),
                where("disaster_status", "==", "active"),
                where("is_deleted", "==", false),
                where("center_id", "==", userData.center_id),
                where(
                  "affected_counties",
                  "array-contains",
                  updatedUserData?.county
                )
              );

              const querySnapshot = await getDocs(q);

              disasters = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                name: doc.data().disaster_name,
              }));

              activeData = querySnapshot.docs.map((doc) => ({
                disaster_id: doc.id,
                disaster_name: doc.data().disaster_name,
                disaster_status: doc.data().disaster_status,
                start_date: doc.data().start_date
                  ? doc.data().start_date.toDate().toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "numeric",
                    })
                  : null,
                county: doc.data().affected_counties,
              }));
            }

            setDisasterNames(disasters);
            setActiveData(activeData);

            if (disasters.length > 0) {
              setSelectedDisaster(disasters[0].name);
              setSelectedDisasterId(disasters[0].id);
            }

            setLoading(false);
          } catch (error) {
            console.error("Error fetching disaster names:", error);
            setLoading(false);
          }
        }
      }
    };

    fetchUserData();
  }, [user, selectedUserId]);

  //FETCH DISASTER COUNTIES
  useEffect(() => {
    const fetchDisaster = async () => {
      if (!selectedDisasterId) {
        console.error("No valid ID provided.");
        return;
      }

      try {
        const docRef = doc(db, "declared-disaster", selectedDisasterId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          const formattedCounties = Array.isArray(data.affected_counties)
            ? data.affected_counties.map((county) => ({
                text: county,
                value: county,
              }))
            : [];

          setSelectedCounties(formattedCounties.map((county) => county.value));
        } else {
          setSelectedCounties([]);
        }
      } catch (error) {
        setSelectedCounties([]);
      }
    };

    fetchDisaster();
  }, [selectedDisasterId]);

  //FETCH GRAPH DATA
  useEffect(() => {
    const loadSurveyData = async (counties) => {
      try {
        const graphData = await DisasterMgtService.fetchDisasterSurvey(
          selectedDisasterId,
          counties
        );

        setGraphData(graphData);
      } catch (error) {
        console.error("Error loading survey data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (selectedCounties.length > 0) {
      loadSurveyData(selectedCounties);
    }
  }, [selectedCounties]);

  //FETCH DISASTER NAMES
  useEffect(() => {
    const fetchDisasterNames = async () => {
      try {
        const querySnapshot = await getDocs(
          collection(db, "declared-disaster")
        );
        const names = querySnapshot.docs.map((doc) => doc.data().disaster_name);
      } catch (error) {
        console.error("Error fetching disaster names:", error);
      }
    };

    fetchDisasterNames();
  }, []);

  const onGetHouseholdMembers = async (id, disater_id) => {
    const householdMembersRef = collection(
      db,
      "users",
      id,
      "disaster_households",
      disater_id,
      "household_members"
    );
    const householdMembers = await getDocs(householdMembersRef);

    return householdMembers.docs.length;
  };

  //FETCH FROM SURVEY_RESPONSE SUBCOLLECTION
  const fetchSurveyAnswerByQuestion = async (questionKey) => {
    if (
      !Array.isArray(affectedUserIds) ||
      affectedUserIds.length === 0 ||
      !selectedDisasterId
    ) {
      return [];
    }

    try {
      const answerFetches = affectedUserIds.map(async (userId) => {
        const responsesRef = collection(
          db,
          "users",
          userId,
          "survey_responses"
        );
        const responsesSnap = await getDocs(responsesRef);

        for (const docSnap of responsesSnap.docs) {
          const data = docSnap.data();

          const isMatchingDisaster = Array.isArray(data.disasters)
            ? data.disasters.some((d) => d.disaster_id === selectedDisasterId)
            : false;

          if (isMatchingDisaster) {
            const questionData = data.responses?.[questionKey];
            const answer = questionData?.answer;
            const followUp = questionData?.followUp;

            if (answer && typeof answer === "object") {
              let processedAnswer = { ...answer };

              if (processedAnswer.Other === true) {
                // Remove Other regardless of followUp presence
                delete processedAnswer.Other;

                // Only add followUp if it exists
                if (followUp) {
                  processedAnswer[followUp] = true;
                }
              }

              return {
                user_id: userId,
                answer: processedAnswer,
              };
            }

            return {
              user_id: userId,
              answer: answer || "No answer",
            };
          }
        }

        return null;
      });

      const results = await Promise.all(answerFetches);
      return results.filter(Boolean);
    } catch (error) {
      console.error(`Error fetching responses for "${questionKey}":`, error);
      return [];
    }
  };

  //QUERY ALL
  const onGetHouseholdAffected = async () => {
    if (
      !Array.isArray(affectedUserIds) ||
      affectedUserIds.length === 0 ||
      !selectedDisasterId
    ) {
      return [];
    }

    try {
      const [
        injuryResults,
        disasterImpactResults,
        propertyDamageResults,
        livingSituationResults,
        housingTypeResults,
        medicalNeedsResults,
        tornadoShelterResults,
        propertyDamageTypeResults,
        immediateNeedsResults,
        waterDepthResults,
        wellWaterResults,
        vehiclesAffectedResults,
        transportationNeedsResults,
        standingWaterResults,
        debrisRemovalResults,
        fallenTreeResults,
        majorAppliancesResults,
        runningWaterResults,
      ] = await Promise.all([
        fetchSurveyAnswerByQuestion(questionKeys.injury),
        fetchSurveyAnswerByQuestion(questionKeys.disasterImpact),
        fetchSurveyAnswerByQuestion(questionKeys.propertyDamage),
        fetchSurveyAnswerByQuestion(questionKeys.livingSituation),
        fetchSurveyAnswerByQuestion(questionKeys.housingType),
        fetchSurveyAnswerByQuestion(questionKeys.medicalNeeds),
        fetchSurveyAnswerByQuestion(questionKeys.tornadoShelter),
        fetchSurveyAnswerByQuestion(questionKeys.propertyDamageType),
        fetchSurveyAnswerByQuestion(questionKeys.immediateNeeds),
        fetchSurveyAnswerByQuestion(questionKeys.waterDepth),
        fetchSurveyAnswerByQuestion(questionKeys.wellWater),
        fetchSurveyAnswerByQuestion(questionKeys.vehiclesAffected),
        fetchSurveyAnswerByQuestion(questionKeys.transportationNeeds),
        fetchSurveyAnswerByQuestion(questionKeys.standingWater),
        fetchSurveyAnswerByQuestion(questionKeys.debrisRemoval),
        fetchSurveyAnswerByQuestion(questionKeys.fallenTree),
        fetchSurveyAnswerByQuestion(questionKeys.majorAppliances),
        fetchSurveyAnswerByQuestion(questionKeys.runningWater),
      ]);

      const makeMap = (arr) =>
        Object.fromEntries(arr.map((i) => [i.user_id, i.answer]));

      const injuryMap = makeMap(injuryResults);
      const disasterImpactMap = makeMap(disasterImpactResults);
      const propertyDamageMap = makeMap(propertyDamageResults);
      const livingSituationMap = makeMap(livingSituationResults);
      const housingTypeMap = makeMap(housingTypeResults);
      const medicalNeedsMap = makeMap(medicalNeedsResults);
      const tornadoShelterMap = makeMap(tornadoShelterResults);
      const propertyDamageTypeMap = makeMap(propertyDamageTypeResults);
      const immediateNeedsMap = makeMap(immediateNeedsResults);
      const waterDepthMap = makeMap(waterDepthResults);
      const wellWaterMap = makeMap(wellWaterResults);
      const vehiclesAffectedMap = makeMap(vehiclesAffectedResults);
      const transportationNeedsMap = makeMap(transportationNeedsResults);
      const standingWaterMap = makeMap(standingWaterResults);
      const debrisRemovalMap = makeMap(debrisRemovalResults);
      const fallenTreeMap = makeMap(fallenTreeResults);
      const majorAppliancesMap = makeMap(majorAppliancesResults);
      const runningWaterMap = makeMap(runningWaterResults);

      // Fetch and merge user data
      const userFetches = affectedUserIds.map(async (userId) => {
        const userDocRef = doc(db, "users", userId);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();

          let ownerRenter = "";
          let homeInsurance = false;
          let automotiveInsurance = false;
          let floodInsurance = false;
          let additionalInsuranceType = "";

          try {
            const ownerDocRef = doc(
              db,
              "users",
              userId,
              "disaster_households",
              selectedDisasterId,
              "household_info",
              "details"
            );
            const ownerDocSnap = await getDoc(ownerDocRef);

            if (ownerDocSnap.exists()) {
              const ownerData = ownerDocSnap.data();

              ownerRenter = ownerData.owner_or_renter || "";
              homeInsurance = ownerData.home_insurance;
              automotiveInsurance = ownerData.automotive_insurance;
              floodInsurance =
                (ownerData.additional_insurance_type === "Flood insurance" &&
                  ownerData.additional_insurance) ||
                false;
              additionalInsuranceType =
                ownerData.additional_insurance_type || "";
            }
          } catch (err) {
            console.warn(
              `Error fetching household info for user ${userId}:`,
              err
            );
          }

          return {
            id: userId,
            email: data.email || "",
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            mobile_number: data.mobile_number || "",
            address1: data.address1 || "",
            address2: data.address2 || "",
            city: data.city || "",
            state: data.state || "",
            zip: data.zip || "",

            displaced: data.is_displaced || "",

            injury: injuryMap[userId] || "",
            disasterImpact: disasterImpactMap[userId] || "",
            propertyDamage: propertyDamageMap[userId] || "",
            livingSituation: livingSituationMap[userId] || "",
            housingType: housingTypeMap[userId] || "",
            medicalNeeds: medicalNeedsMap[userId] || "",
            tornadoShelter: tornadoShelterMap[userId] || "",
            propertyDamageType: propertyDamageTypeMap[userId] || "",
            immediateNeeds: immediateNeedsMap[userId] || "",
            waterDepth: waterDepthMap[userId] || "",
            wellWater: wellWaterMap[userId] || "",
            vehiclesAffected: vehiclesAffectedMap[userId] || "",
            transportationNeeds: transportationNeedsMap[userId] || "",
            ownerRenter: ownerRenter,
            homeInsurance: homeInsurance,
            automotiveInsurance: automotiveInsurance,
            floodInsurance: floodInsurance,
            additionalInsuranceType: additionalInsuranceType,
            runningWater: runningWaterMap[userId] || "",
            standingWater: standingWaterMap[userId] || "",
            debrisRemoval: debrisRemovalMap[userId] || "",
            fallenTree: fallenTreeMap[userId] || "",
            majorAppliances: majorAppliancesMap[userId] || "",
          };
        }

        return null;
      });

      const results = await Promise.all(userFetches);
      console.log("Final processed users:", results); // Debug log
      return results.filter(Boolean);
    } catch (error) {
      console.error("Error fetching enriched affected user data:", error);
      return [];
    }
  };

  useEffect(() => {
    const fetchAndLogAffectedUsers = async () => {
      const enrichedUsers = await onGetHouseholdAffected();
      setEnrichedUsers(enrichedUsers);
      console.log("Affected Users:", enrichedUsers);
    };

    if (affectedUserIds?.length > 0 && selectedDisasterId) {
      fetchAndLogAffectedUsers();
    }
  }, [affectedUserIds, selectedDisasterId]);

  const handleCardSelect = (cardType) => {
    setSelectedCardType(cardType);
  };

  const filterOptions = [
    { text: "Well Water Status", value: "wellWater" },
    { text: "Vehicles Affected", value: "vehiclesAffected" },
    { text: "Has reliable transportation", value: "transportationNeeds" },
    { text: "Injury Status", value: "injury" },
    { text: "Property Damage", value: "propertyDamage" },
    { text: "Owner/Renter Status", value: "ownerRenter" },
    { text: "Displacement Status", value: "displaced" },
    { text: "Current Living Situation", value: "livingSituation" },
    { text: "Housing Type", value: "housingType" },
    { text: "Medical/Mobility Needs", value: "medicalNeeds" },
    { text: "Took Shelter", value: "tornadoShelter" },
    { text: "Property Damage Types", value: "propertyDamageType" },
    { text: "Immediate Needs", value: "immediateNeeds" },
    { text: "Water Depth", value: "waterDepth" },
    { text: "Standing Water Inside Home", value: "standingWater" },
    { text: "Insurance Types", value: "insuranceType" },
    { text: "Debris Removal", value: "debrisRemoval" },
    { text: "Home Hit by Fallen Trees", value: "fallenTree" },
    { text: "Major Appliances", value: "majorAppliances" },
    { text: "Running Water", value: "runningWater" },
  ];

  const getResponseTemplate = (rowData) => {
    const value = rowData[selectedFilter];

    if (selectedFilter === "insuranceType") {
      const hasHomeInsurance = Boolean(rowData.homeInsurance);
      const hasAutoInsurance = Boolean(rowData.automotiveInsurance);
      const hasFloodInsurance = Boolean(rowData.floodInsurance);

      let insuranceTypes = [];
      if (hasHomeInsurance) insuranceTypes.push("home");
      if (hasAutoInsurance) insuranceTypes.push("automotive");
      if (hasFloodInsurance) insuranceTypes.push("flood");

      if (insuranceTypes.length === 0) {
        return "No insurance coverage";
      }

      if (insuranceTypes.length === 1) {
        return `Covered only by ${insuranceTypes[0]} insurance`;
      }

      const lastType = insuranceTypes.pop();
      return `Covered only by ${insuranceTypes.join(", ")}${
        insuranceTypes.length ? ", and " : " and "
      }${lastType} insurance`;
    }

    // Handle displaced status
    if (selectedFilter === "displaced") {
      return value === true ? "Displaced" : "Not displaced";
    }

    // Handle medical needs specifically
    if (selectedFilter === "medicalNeeds") {
      return value.replace(/\./g, ".\n").replace(/\n$/, "");
    }

    // Handle waterDepth specifically
    if (selectedFilter === "waterDepth") {
      return value.replace(/\(e\.g\./g, "\n(e.g.");
    }

    // Handle transportationNeeds specifically
    if (selectedFilter === "transportationNeeds") {
      return value || "N/A";
    }

    // Handle runningWater specifically
    if (selectedFilter === "runningWater") {
      return value || "N/A";
    }

    // Handle object types (homeDamageType, immediateNeeds, fallenTree, majorAppliances)
    if (
      selectedFilter === "propertyDamageType" ||
      selectedFilter === "immediateNeeds" ||
      selectedFilter === "fallenTree" ||
      selectedFilter === "majorAppliances" ||
      selectedFilter === "runningWater"
    ) {
      if (typeof value === "object") {
        let items = Object.entries(value)
          .filter(
            ([key, val]) =>
              val === true && key !== "followUp" && key !== "Other"
          )
          .map(([key]) => key);

        if (items.length === 0) return "None";

        // Always join with comma and space, regardless of line breaks
        return items.join(", ");
      }
    }

    // Handle debrisRemoval (assuming it's a boolean or yes/no)
    if (selectedFilter === "debrisRemoval") {
      if (typeof value === "boolean") {
        return value ? "Yes" : "No";
      }
      return value || "N/A";
    }

    // Handle fallenTree specifically
    if (typeof value === "object") {
      // Get array of keys where value is true
      let answers = Object.entries(value)
        .filter(([key, val]) => val === true)
        .map(([key]) => key);

      // If Check All is true, remove Check All, No and N/A
      if (value["Check All"] === true) {
        answers = answers.filter(
          (answer) =>
            answer !== "Check All" && answer !== "No" && answer !== "N/A"
        );
      }

      // Join remaining answers
      const cleanedValue = answers.join(", ");
      return cleanedValue || "N/A";
    }

    // For string values, add line breaks after commas
    if (typeof value === "string" && value.includes(",")) {
      return value.split(", ").join("\n");
    }

    return value;
  };

  // Add export handler
  const handleExport = () => {
    if (!enrichedUsers?.length) return;

    const columns = [
      { field: "name", headerText: "Name" },
      { field: "email", headerText: "Email" },
      { field: "mobile_number", headerText: "Mobile Number" },
      { field: "address1", headerText: "Address" },
      { field: selectedFilter, headerText: "Response" },
    ];

    const dataToExport = enrichedUsers.map((user) => {
      const exportObj = {};
      columns.forEach((col) => {
        if (col.field === "name") {
          exportObj[col.headerText] =
            `${user.first_name || ""} ${user.last_name || ""}`.trim() || "N/A";
        } else if (col.field === "address1") {
          const parts = [
            user.address1,
            user.address2,
            user.city,
            user.state,
            user.zip,
          ].filter(Boolean);
          exportObj[col.headerText] = parts.join(", ") || "N/A";
        } else if (col.field === selectedFilter) {
          // Process the response using the same template function used in the grid
          exportObj[col.headerText] = getResponseTemplate(user);
        } else {
          exportObj[col.headerText] = user[col.field] || "N/A";
        }
      });
      return exportObj;
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dataToExport);

    // Auto-size columns
    const colWidths = [];
    dataToExport.forEach((row) => {
      Object.entries(row).forEach(([key, value], colIndex) => {
        const cellLength = Math.max(String(value).length, String(key).length);
        colWidths[colIndex] = Math.max(colWidths[colIndex] || 0, cellLength);
      });
    });

    ws["!cols"] = colWidths.map((width) => ({
      wch: width + 2, // Add some padding
    }));

    XLSX.utils.book_append_sheet(wb, ws, "Data");

    const fileName = `Disaster_Report_${selectedFilter}_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Add PIN fetching logic in useEffect
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // Fetch the user's PIN
        const userDocRef = doc(db, "users", selectedUserId);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          if (role === "center") {
            const centerDocRef = doc(db, "centers", userDoc.data().center_id);
            const centerDoc = await getDoc(centerDocRef);
            if (centerDoc.exists() && centerDoc.data().pin) {
              setCorrectPIN(decrypt(centerDoc.data().pin));
              return;
            }
          } else if (role === "partner") {
            const partnerDocRef = doc(
              db,
              "partners",
              userDoc.data().partner_id
            );
            const partnerDoc = await getDoc(partnerDocRef);
            if (partnerDoc.exists() && partnerDoc.data().pin) {
              setCorrectPIN(decrypt(partnerDoc.data().pin));
              return;
            }
          }
        }
        // If no PIN is set, it will use the default '1111'
      }
    });

    return () => unsubscribe();
  }, []);

  // Add PIN handling functions
  const handleUnlock = async () => {
    if (enteredPIN === correctPIN) {
      setIsUnlocked(true);
      setShowPinInstructions(false);
      localStorage.setItem("pinMessageShown", "true");
    } else {
      setWrongPinMessage(true);
      setEnteredPIN("");
      setInputValues(["", "", "", ""]);
      inputRefs.current[0].focus();

      setTimeout(() => {
        setWrongPinMessage(false);
      }, 3000);
    }
  };

  const onInput = (index, event) => {
    const value = event.target.value;
    if (/^\d$/.test(value)) {
      const newValues = [...inputValues];
      newValues[index] = value;
      setInputValues(newValues);
      setEnteredPIN(newValues.join(""));

      if (index < inputRefs.current.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const onKeyDown = (index, event) => {
    if (event.key === "Backspace") {
      event.preventDefault();

      const newValues = [...inputValues];

      if (inputValues[index]) {
        newValues[index] = "";
      } else if (index > 0) {
        newValues[index - 1] = "";
        inputRefs.current[index - 1].focus();
      }

      setInputValues(newValues);
      setEnteredPIN(newValues.join(""));
    }
  };

  const handleGoBack = () => {
    window.history.back();
    window.history.back();
  };

  // Modify the return statement to show PIN entry first
  if (!isUnlocked) {
    return (
      <div className="absolute inset-0 z-50 flex items-center justify-center bg-opacity-10 backdrop-blur-md">
        <div className="absolute right-4 top-4">
          <button
            onClick={() => setShowPinInstructions(!showPinInstructions)}
            className="hover:bg-gray-100 inline-flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 bg-gray-200 text-sm text-gray-500 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            title="Show PIN instructions"
          >
            ?
          </button>
          {showPinInstructions && (
            <div className="absolute right-0 top-10 z-10 w-64 rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
              <p className="text-gray-600 mb-2 text-sm font-medium">
                To view your Lockbox PIN:
              </p>
              <ol className="text-gray-600 ml-4 list-decimal text-left text-sm">
                <li>Click on your profile icon in the top right</li>
                <li>Select "Show Lockbox PIN"</li>
                <li>Enter your account password to reveal the PIN</li>
              </ol>
            </div>
          )}
        </div>
        <div className="flex flex-col items-center justify-center rounded-lg p-8">
          <img
            src={LockImage}
            alt="Lockbox"
            className="mb-6 h-[290px] w-[290px]"
          />
          <div className="mb-4 text-center text-sm font-medium text-red-600">
            <strong>Note:</strong> This section contains personal data, please
            enter your PIN to proceed.
          </div>

          <div className="mb-4 flex space-x-8">
            {inputValues.map((value, index) => (
              <input
                key={index}
                type="password"
                maxLength="1"
                className="lockbox-input h-12 w-12 rounded-xl border border-gray-300 bg-gray-300 text-center text-2xl"
                pattern="[0-9]"
                value={value}
                onChange={(event) => onInput(index, event)}
                onKeyDown={(event) => onKeyDown(index, event)}
                ref={(el) => (inputRefs.current[index] = el)}
              />
            ))}
          </div>
          {wrongPinMessage && (
            <div className="mb-4 text-sm text-red-600">
              Wrong PIN entered, please try again.
            </div>
          )}
          <div className="flex w-full space-x-4">
            <button
              onClick={handleGoBack}
              className="btn-cancel flex flex-1 cursor-pointer items-center justify-center px-4 py-2"
            >
              <span className="mr-2">←</span> Go Back
            </button>
            <button
              onClick={handleUnlock}
              className="btn-default flex flex-1 cursor-pointer items-center justify-center px-4 py-2"
            >
              <svg
                width="18"
                height="21"
                viewBox="0 0 18 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2"
              >
                <g id="Shield Done">
                  <path
                    id="Fill 1"
                    opacity="0.4"
                    d="M9.08649 20.5C8.96266 20.5 8.83884 20.4716 8.72709 20.4137L5.12599 18.5496C4.10415 18.0201 3.30481 17.4259 2.68063 16.7336C1.31449 15.2195 0.554402 13.276 0.542321 11.2599L0.500038 4.62426C0.495004 3.85842 0.989312 3.17103 1.72826 2.91215L8.3405 0.606788C8.73313 0.466564 9.17106 0.464603 9.57073 0.599924L16.2081 2.82684C16.9511 3.07493 17.4535 3.75742 17.4575 4.52228L17.4998 11.1628C17.5129 13.176 16.779 15.1274 15.434 16.6581C14.8168 17.3602 14.0245 17.9632 13.0128 18.5025L9.44388 20.4088C9.33314 20.4686 9.21032 20.499 9.08649 20.5Z"
                    fill="white"
                  />
                  <path
                    id="Fill 4"
                    d="M8.31936 12.8209C8.12607 12.8219 7.93278 12.7523 7.78378 12.6091L5.86695 10.7656C5.57097 10.4793 5.56795 10.0145 5.86091 9.72617C6.15387 9.4369 6.63207 9.43396 6.92906 9.71931L8.30829 11.0451L11.6758 7.72479C11.9698 7.43552 12.448 7.43258 12.744 7.71793C13.041 8.00426 13.044 8.47004 12.751 8.75735L8.85193 12.6022C8.70494 12.7474 8.51266 12.8199 8.31936 12.8209Z"
                    fill="white"
                  />
                </g>
              </svg>
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`m-0 box-border flex w-[1648px] max-w-full flex-col items-start justify-start gap-2.5 px-2.5 py-[15px] leading-[normal] tracking-[normal] ${className}`}
    >
      {disasterNames.length > 0 ? (
        <div className="w-full flex-1 rounded-mini bg-white">
          {/* Report Generator Start */}
          <div
            className="fixed left-0 top-1 z-20 ml-[90vh] flex items-center justify-center gap-1 p-1"
            ref={dropdownRef}
          >
            <div className="relative w-[300px] cursor-pointer rounded px-2 hover:bg-slate-400">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="relative flex w-full items-center justify-between gap-1.5 bg-transparent px-0 py-2 text-[17px] text-white"
              >
                <span className="truncate">
                  {selectedDisaster ? selectedDisaster : "Select a Disaster"}
                </span>
                <ChevronDown className="h-4 w-4" strokeWidth={2} />
              </button>

              {isDropdownOpen && (
                <div className="absolute left-1/2 z-10 mt-1 min-w-[400px] -translate-x-1/2 rounded border border-gray-300 bg-white shadow-lg">
                  <div className="max-h-60 overflow-y-auto">
                    {disasterNames.map((disaster) => (
                      <div
                        key={disaster.id}
                        className="hover:bg-gray-100 cursor-pointer px-4 py-2"
                        onClick={() => {
                          setSelectedDisasterId(disaster.id);
                          setSelectedDisaster(disaster.name);
                          setIsDropdownOpen(false);
                        }}
                      >
                        {disaster.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="">
            <HeaderTitle title="Report Generator" />

            {enrichedUsers && (
              <div className="mt-8">
                <div className="mb-4 flex items-center justify-between">
                  <div className="w-64">
                    <DropDownListComponent
                      dataSource={filterOptions}
                      fields={{ text: "text", value: "value" }}
                      value={selectedFilter}
                      change={(e) => setSelectedFilter(e.value)}
                      placeholder="Select a filter"
                      cssClass="e-outline"
                    />
                  </div>
                  <button
                    onClick={handleExport}
                    className="cursor-pointer rounded-[4px] bg-[#003460] px-4 py-2 font-['Inter'] text-base font-medium text-[#F7CA41] hover:bg-[#003460]/90"
                  >
                    Export
                  </button>
                </div>
                <GridComponent
                  dataSource={enrichedUsers}
                  allowPaging={true}
                  pageSettings={pageSettings}
                  cssClass="new-grid-color h-full wrap-column allow-overflow-grid-text"
                  height="auto"
                >
                  <ColumnsDirective>
                    <ColumnDirective
                      field="first_name"
                      headerText="Name"
                      width="100"
                      template={(rowData) => {
                        return (
                          `${rowData.first_name || ""} ${
                            rowData.last_name || ""
                          }`.trim() || "N/A"
                        );
                      }}
                    />
                    <ColumnDirective
                      field="email"
                      headerText="Email"
                      width="200"
                    />
                    <ColumnDirective
                      field="mobile_number"
                      headerText="Phone"
                      width="120"
                    />
                    <ColumnDirective
                      field="address1"
                      headerText="Address"
                      width="200"
                      template={(rowData) => {
                        const parts = [
                          rowData.address1,
                          rowData.address2,
                          rowData.city,
                          rowData.state,
                          rowData.zip,
                        ].filter(Boolean);
                        return parts.join(", ") || "N/A";
                      }}
                    />
                    <ColumnDirective
                      field={selectedFilter}
                      headerText="Response"
                      width="200"
                      template={getResponseTemplate}
                      textAlign="Left"
                      clipMode="EllipsisWithTooltip"
                      wrapMode="Normal"
                      allowHtml={false}
                    />
                  </ColumnsDirective>
                  <Inject services={[Page]} />
                </GridComponent>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const styles = `
.wrap-column .e-grid .e-rowcell {
  white-space: pre-wrap !important;
  line-height: 1.5 !important;
  padding: 8px !important;
}

.wrap-column .e-grid .e-headercell {
  white-space: pre-wrap !important;
  line-height: 1.5 !important;
}
`;

export default ReportGenerator;
